import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "fw-semibold text-primary-dark" }
const _hoisted_2 = { role: "tab" }
const _hoisted_3 = { class: "d-flex justify-content-between align-items-center w-100" }
const _hoisted_4 = ["innerHTML"]
const _hoisted_5 = { class: "sr-only" }
const _hoisted_6 = ["data-test-ref", "innerHTML"]

import { computed, ref, watch, onUnmounted } from 'vue';
import { useI18n } from '@/i18n';
import { formatPhoneNumber } from '@/utils/format';
import DOMPurify from 'dompurify';
import Uuid from '@/utils/uuid';

interface FaqSection {
    title: string;
    content: string;
    formattedContent?: string;
    expanded: boolean;
}

interface FaqSectionAccordionState extends FaqSection {
    id: string;
    expanded: boolean;
}

/*-----*\
| Props |
\*-----*/


const __default__ = {
    compatConfig: {
        COMPONENT_V_MODEL: false,
    },
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'multi-factor-auth-faq-modal',
  props: {
    modelValue: {
        type: Boolean,
        default: false,
    },
},
  emits: ['update:modelValue'],
  setup(__props, { emit }) {

const props = __props;

const i18n = useI18n();
const { t, tm } = i18n;

const phoneNumber = t('mfa.faq.phoneNumber');



onUnmounted(() => {
    accordionItems.value = [];
});
/*-----*\
| Emits |
\*-----*/



/*----*\
| Data |
\*----*/

const showModal = ref(false);
const accordionItems = ref<FaqSectionAccordionState[]>([]);
const guid = ref(new Uuid().toString());

/*--------*\
| Computed |
\*--------*/

const faqSections = computed<FaqSectionAccordionState[]>(() => {
    const questions = tm('mfa.faq.questions') as FaqSection[];
    return questions.map((section, index) => ({
        ...section,
        id: `${index}`,
        expanded: accordionItems.value.find((item) => item.id === `${index}`)?.expanded ?? false,
        formattedContent: DOMPurify.sanitize(
            section.content.replace(
                '{phoneNumber}',
                `<a href="tel:${phoneNumber}">${formatPhoneNumber(phoneNumber)}</a>`
            )
        ),
    }));
});

/*--------*\
| Watchers |
\*--------*/

watch(
    () => props.modelValue,
    (value) => {
        showModal.value = value;
    },
    { immediate: true }
);

watch(
    () => tm('mfa.faq.questions'),
    (questions) => {
        if (questions?.length > 0) {
            let idCounter = 0;
            accordionItems.value = (questions as FaqSection[]).map((section) => ({
                title: section.title,
                content: section.content,
                formattedContent: DOMPurify.sanitize(
                    section.content.replace(
                        '{phoneNumber}',
                        `<a href="tel:${phoneNumber}">${formatPhoneNumber(phoneNumber)}</a>`
                    )
                ),
                id: `${idCounter++}`,
                expanded: accordionItems.value.find((item) => item.id === `${idCounter - 1}`)?.expanded ?? false,
            }));
        } else {
            accordionItems.value = [];
        }
    },
    { immediate: true }
);

/*-------*\
| Methods |
\*-------*/

const onExpandQuestion = (id: string) => {
    accordionItems.value = accordionItems.value.map((section) => {
        if (section.id === id) {
            return { ...section, expanded: !section.expanded };
        }
        return section;
    });
};

const onModalHidden = () => {
    emit('update:modelValue', false);
    accordionItems.value = accordionItems.value.map((section) => ({
        ...section,
        expanded: false,
    }));
};

return (_ctx: any,_cache: any) => {
  const _component_b_btn = _resolveComponent("b-btn")!
  const _component_b_button_group = _resolveComponent("b-button-group")!
  const _component_b_collapse = _resolveComponent("b-collapse")!
  const _component_b_modal = _resolveComponent("b-modal")!

  return (_openBlock(), _createBlock(_component_b_modal, {
    size: "lg",
    visible: showModal.value,
    "ok-only": "",
    scrollable: "",
    "data-test-ref": "multi-factor-auth-faq-modal-container",
    onShown: _cache[0] || (_cache[0] = ($event: any) => (emit('update:modelValue', true))),
    onHidden: onModalHidden
  }, {
    "modal-title": _withCtx(() => [
      _createElementVNode("h2", _hoisted_1, _toDisplayString(_unref(t)('mfa.faq.modalTitle')), 1 /* TEXT */)
    ]),
    default: _withCtx(() => [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(faqSections), (faqSection) => {
        return (_openBlock(), _createElementBlock("div", {
          key: faqSection.id,
          role: "tablist"
        }, [
          _createElementVNode("div", _hoisted_2, [
            _createVNode(_component_b_button_group, {
              block: "",
              class: "w-100 pr-3 my-1"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_b_btn, {
                  block: "",
                  variant: "link",
                  class: _normalizeClass(["h3 text-decoration-none border-bottom text-left align-self-center pl-0 pt-2 text-primary-dark", faqSection.expanded ? 'font-weight-bold text-muted'
                            : 'font-weight-bold text-muted collapsed']),
                  "aria-expanded": faqSection.expanded ? 'true' : 'false',
                  "aria-controls": guid.value + '-' + faqSection.id,
                  "data-test-ref": 'faq-list-item-button' + faqSection.id,
                  onClick: ($event: any) => (onExpandQuestion(faqSection.id))
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_3, [
                      _createElementVNode("h4", {
                        innerHTML: faqSection.title
                      }, null, 8 /* PROPS */, _hoisted_4),
                      _createElementVNode("i", {
                        class: _normalizeClass([faqSection.expanded ? 'fa fa-chevron-up' : 'fa fa-chevron-down-thin']),
                        "aria-hidden": "true"
                      }, null, 2 /* CLASS */)
                    ]),
                    _createElementVNode("span", _hoisted_5, _toDisplayString(_unref(t)('mfa.faq.showHideText', 'Show/Hide Answer')), 1 /* TEXT */)
                  ], undefined, true),
                  _: 2 /* DYNAMIC */
                }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["class", "aria-expanded", "aria-controls", "data-test-ref", "onClick"])
              ], undefined, true),
              _: 2 /* DYNAMIC */
            }, 1024 /* DYNAMIC_SLOTS */)
          ]),
          _createVNode(_component_b_collapse, {
            id: guid.value + '-' + faqSection.id,
            modelValue: faqSection.expanded,
            "onUpdate:modelValue": ($event: any) => ((faqSection.expanded) = $event),
            role: "tabpanel",
            class: "pt-2 pr-3 pd-2"
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", {
                "data-test-ref": 'faq-list-item-answer' + faqSection.id,
                innerHTML: faqSection.formattedContent
              }, null, 8 /* PROPS */, _hoisted_6)
            ], undefined, true),
            _: 2 /* DYNAMIC */
          }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["id", "modelValue", "onUpdate:modelValue"])
        ]))
      }), 128 /* KEYED_FRAGMENT */))
    ], undefined, true),
    _: 1 /* STABLE */
  }, 8 /* PROPS */, ["visible"]))
}
}

})